import React from "react";
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Paper,
  Typography,
  Divider,
} from "@mui/material";
import { OrangeTextButton } from "../../common/OrangeTextButton";
import {
  ParentAccount,
  WireGuardFilterState,
  WireGuardServerOption,
} from "./wireGuardTunnelsInterfaces";

interface Props {
  filters: WireGuardFilterState;
  setFilters: React.Dispatch<React.SetStateAction<WireGuardFilterState>>;
  parentAccounts: ParentAccount[];
  wireGuardOptions: WireGuardServerOption[];
  tunnelStatuses: string[];
}

const defaultFilters: WireGuardFilterState = {
  groupIds: [],
  serverIds: [],
  tunnelStatuses: [],
  subnetVirtuallyAssigned: "all",
  searchText: "",
};

export const WireGuardTunnelsFiltersPanel = ({
  filters,
  setFilters,
  parentAccounts,
  wireGuardOptions,
  tunnelStatuses,
}: Props) => {
  const toggleNumberFilter = (
    key: "groupIds" | "serverIds",
    value: number,
    checked: boolean
  ) => {
    setFilters((prev) => ({
      ...prev,
      [key]: checked ? [value] : [],
    }));
  };

  const toggleStatusFilter = (value: string, checked: boolean) => {
    setFilters((prev) => ({
      ...prev,
      tunnelStatuses: checked ? [value] : [],
    }));
  };

  const setSubnetVirtuallyAssignedFilter = (
    value: WireGuardFilterState["subnetVirtuallyAssigned"]
  ) => {
    setFilters((prev) => ({
      ...prev,
      subnetVirtuallyAssigned: value,
    }));
  };

  return (
    <Paper
      sx={{
        width: 340,
        minWidth: 340,
        height: "100%",
        maxHeight: "100%",
        px: 2,
        pb: 2,
        pt: 0,
        borderRadius: 2,
        borderTop: "1px solid #d1d5db",
        overflowY: "auto",
        fontFamily: "Mulish",
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={0.5}
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 2,
          backgroundColor: "white",
          pt: 1,
          pb: 0.5,
          height: 46,
          borderBottom: "1px solid #d1d5db",
        }}
      >
        <Typography sx={{ fontFamily: "Mulish", fontWeight: 700, fontSize: 14 }}>
          FILTERS
        </Typography>
        <Box
          sx={{
            transform: "scale(0.82)",
            transformOrigin: "right center",
            mr: "-10px",
          }}
        >
          <OrangeTextButton
            onClick={() => setFilters(defaultFilters)}
            text="Reset"
            disabled={false}
          />
        </Box>
      </Box>
      <Box display="flex" flexDirection="column" gap={2} pt={1}>
        <Box>
          <Typography sx={{ fontFamily: "Mulish", fontWeight: 700, fontSize: 15 }}>
            Group
          </Typography>
          <FormGroup sx={{ maxHeight: 180, overflowY: "auto" }}>
            <FormControlLabel
              sx={{ m: 0 }}
              control={
                <Checkbox
                  checked={filters.groupIds.length === 0}
                  onChange={() =>
                    setFilters((prev) => ({
                      ...prev,
                      groupIds: [],
                    }))
                  }
                  size="small"
                />
              }
              label={
                <Typography sx={{ fontFamily: "Mulish", fontSize: 14 }}>
                  All Groups
                </Typography>
              }
            />
            {parentAccounts.map((account) => (
              <FormControlLabel
                key={account.id}
                sx={{ m: 0 }}
                control={
                  <Checkbox
                    checked={filters.groupIds.includes(account.id)}
                    onChange={(event) =>
                      toggleNumberFilter("groupIds", account.id, event.target.checked)
                    }
                    size="small"
                  />
                }
                label={
                  <Typography sx={{ fontFamily: "Mulish", fontSize: 14 }}>
                    {account.name}
                  </Typography>
                }
              />
            ))}
          </FormGroup>
        </Box>
        <Divider />
        <Box>
          <Typography sx={{ fontFamily: "Mulish", fontWeight: 700, fontSize: 15 }}>
            WireGuard Server
          </Typography>
          <FormGroup sx={{ maxHeight: 280, overflowY: "auto" }}>
            <FormControlLabel
              sx={{ m: 0 }}
              control={
                <Checkbox
                  checked={filters.serverIds.length === 0}
                  onChange={() =>
                    setFilters((prev) => ({
                      ...prev,
                      serverIds: [],
                    }))
                  }
                  size="small"
                />
              }
              label={
                <Typography sx={{ fontFamily: "Mulish", fontSize: 14 }}>
                  All Servers
                </Typography>
              }
            />
            {wireGuardOptions.map((option) => (
              <FormControlLabel
                key={option.id}
                sx={{ m: 0 }}
                control={
                  <Checkbox
                    checked={filters.serverIds.includes(option.id)}
                    onChange={(event) =>
                      toggleNumberFilter("serverIds", option.id, event.target.checked)
                    }
                    size="small"
                  />
                }
                label={
                  <Typography sx={{ fontFamily: "Mulish", fontSize: 14 }}>
                    {option.name}
                  </Typography>
                }
              />
            ))}
          </FormGroup>
        </Box>
        <Divider />
        <Box>
          <Typography sx={{ fontFamily: "Mulish", fontWeight: 700, fontSize: 15 }}>
            Tunnel Status
          </Typography>
          <FormGroup sx={{ maxHeight: 180, overflowY: "auto" }}>
            <FormControlLabel
              sx={{ m: 0 }}
              control={
                <Checkbox
                  checked={filters.tunnelStatuses.length === 0}
                  onChange={() =>
                    setFilters((prev) => ({
                      ...prev,
                      tunnelStatuses: [],
                    }))
                  }
                  size="small"
                />
              }
              label={
                <Typography sx={{ fontFamily: "Mulish", fontSize: 14 }}>
                  All
                </Typography>
              }
            />
            {tunnelStatuses.map((status) => (
              <FormControlLabel
                key={status}
                sx={{ m: 0 }}
                control={
                  <Checkbox
                    checked={filters.tunnelStatuses.includes(status)}
                    onChange={(event) =>
                      toggleStatusFilter(status, event.target.checked)
                    }
                    size="small"
                  />
                }
                label={
                  <Typography sx={{ fontFamily: "Mulish", fontSize: 14 }}>
                    {status}
                  </Typography>
                }
              />
            ))}
          </FormGroup>
        </Box>
        <Divider />
        <Box>
          <Typography sx={{ fontFamily: "Mulish", fontWeight: 700, fontSize: 15 }}>
            Subnet Virtually Assigned
          </Typography>
          <FormGroup>
            <FormControlLabel
              sx={{ m: 0 }}
              control={
                <Checkbox
                  checked={
                    filters.subnetVirtuallyAssigned !== "yes" &&
                    filters.subnetVirtuallyAssigned !== "no"
                  }
                  onChange={() => setSubnetVirtuallyAssignedFilter("all")}
                  size="small"
                />
              }
              label={
                <Typography sx={{ fontFamily: "Mulish", fontSize: 14 }}>
                  All
                </Typography>
              }
            />
            <FormControlLabel
              sx={{ m: 0 }}
              control={
                <Checkbox
                  checked={filters.subnetVirtuallyAssigned === "yes"}
                  onChange={() => setSubnetVirtuallyAssignedFilter("yes")}
                  size="small"
                />
              }
              label={
                <Typography sx={{ fontFamily: "Mulish", fontSize: 14 }}>
                  Yes
                </Typography>
              }
            />
            <FormControlLabel
              sx={{ m: 0 }}
              control={
                <Checkbox
                  checked={filters.subnetVirtuallyAssigned === "no"}
                  onChange={() => setSubnetVirtuallyAssignedFilter("no")}
                  size="small"
                />
              }
              label={
                <Typography sx={{ fontFamily: "Mulish", fontSize: 14 }}>
                  No
                </Typography>
              }
            />
          </FormGroup>
        </Box>
      </Box>
    </Paper>
  );
};
